/**
 * The tap grammar (C6) + tap-anchored bookmark windows (C7) — the contract
 * between the physio app's tap bar / offline buffer and the API's append-only
 * tap ledger (migration 004, an ADR-0010 extension).
 *
 * The load-bearing facts, fixed here so no surface can drift on them:
 *   - Tap ids are DEVICE-minted UUIDv7 (ids.ts): the offline buffer re-syncs
 *     after crashes, and replay is idempotent by INSERT on the id — the same
 *     tap can arrive twice and lands once.
 *   - `tappedAt` is the CLIENT tap time — the clinical fact (ADR-0010 §3).
 *     The server records its own receipt time beside it, never over it, so an
 *     offline tap syncing hours later still says when the physio tapped.
 *   - Set-scoped taps bind to a template exercise id (plan §2.2); the codes
 *     themselves are clinical content and live in @advaitnetra/protocol —
 *     this package checks only the shape.
 */

import { z } from 'zod';

/**
 * The tap grammar. Deliberately closed: a new tap kind is a schema + migration
 * change, not a stringly-typed slide. The original five (migration 004) gained
 * two words in increment 5 PR2 (migration 009; ADR-0010 "live-time
 * corrections" extension): 'modified-mark', assisted-mark's sibling (IEC v2
 * parity), and 'undo' — the D3 negation, an appended row stating another
 * row's event void. Both rows stay forever; nothing is deleted.
 */
export const TAP_KINDS = [
  'start-set',
  'log-rep',
  'end-set',
  'log-pain',
  'assisted-mark',
  'modified-mark',
  'undo',
] as const;
export type TapKind = (typeof TAP_KINDS)[number];

/** Tap kinds that must name the exercise they belong to. */
export const SET_SCOPED_TAP_KINDS = [
  'start-set',
  'log-rep',
  'end-set',
] as const satisfies readonly TapKind[];

/**
 * Tap kinds an undo may negate (D3). Start Set and End Set are grammar
 * boundaries — non-undoable by recorded decision (the deliberate narrowing vs
 * the prototype, DISCREPANCIES-V2) — and negations are terminal: an undo can
 * never target an undo. A physio who meant the fact after all taps it again,
 * a new row at its honestly later time.
 */
export const UNDOABLE_TAP_KINDS = [
  'log-rep',
  'log-pain',
  'assisted-mark',
  'modified-mark',
] as const satisfies readonly TapKind[];
export type UndoableTapKind = (typeof UNDOABLE_TAP_KINDS)[number];

/**
 * How a recorded fact entered the record (ADR-0010 §3) — same vocabulary as
 * session_state_events. 'own-tap' is the entire tap bar today;
 * 'confirmed-edit' arrives with the D-increment confirmation gate.
 */
export const EVENT_PROVENANCES = ['own-tap', 'typed-entry', 'confirmed-edit', 'system'] as const;
export type EventProvenance = (typeof EVENT_PROVENANCES)[number];

/** Shape of a template exercise code. The valid CODES live in @advaitnetra/protocol. */
export const exerciseCodeShapeSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9]{1,7}$/, 'malformed exercise code');

/** One tap as the device recorded it (offline buffer entry / request item). */
export const tapInputSchema = z
  .object({
    /** Device-minted UUIDv7 — the idempotency key for offline replay. */
    id: z.uuid(),
    kind: z.enum(TAP_KINDS),
    exerciseCode: exerciseCodeShapeSchema.optional(),
    /** Client tap time — the clinical fact. Never rewritten by sync lag. */
    tappedAt: z.iso.datetime(),
    /**
     * Kind-specific payload (e.g. { painScore } for log-pain). Stored verbatim.
     * Live corrections (ADR-0010 extension ¶2–3) assert their target here:
     * `correctionOf` is the id of the row this one negates ('undo') or
     * restates ('log-pain' + painScore — the D12 at-tap score). The server
     * GRANTS the supersedes linkage from this assertion; when arbitration
     * withholds it, the row still lands with its intent readable right here.
     */
    detail: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((tap, ctx) => {
    if ((SET_SCOPED_TAP_KINDS as readonly string[]).includes(tap.kind) && !tap.exerciseCode) {
      ctx.addIssue({
        code: 'custom',
        message: `${tap.kind} must name its exercise`,
        path: ['exerciseCode'],
      });
    }
    const correctionOf = tap.detail?.['correctionOf'];
    if (tap.kind === 'undo' && correctionOf === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'an undo must name the tap it negates (detail.correctionOf)',
        path: ['detail', 'correctionOf'],
      });
    }
    if (correctionOf !== undefined) {
      if (!z.uuid().safeParse(correctionOf).success) {
        ctx.addIssue({
          code: 'custom',
          message: 'detail.correctionOf must be a tap id',
          path: ['detail', 'correctionOf'],
        });
      }
      if (tap.kind !== 'undo' && tap.kind !== 'log-pain') {
        ctx.addIssue({
          code: 'custom',
          message: `a ${tap.kind} tap cannot carry a live correction`,
          path: ['detail', 'correctionOf'],
        });
      }
    }
    const painScore = tap.detail?.['painScore'];
    if (painScore !== undefined) {
      if (tap.kind !== 'log-pain') {
        ctx.addIssue({
          code: 'custom',
          message: 'painScore belongs to log-pain taps only',
          path: ['detail', 'painScore'],
        });
      } else if (!z.number().int().min(0).max(10).safeParse(painScore).success) {
        ctx.addIssue({
          code: 'custom',
          message: 'painScore is the NRS: a whole number 0–10',
          path: ['detail', 'painScore'],
        });
      }
    }
  });
export type TapInput = z.infer<typeof tapInputSchema>;

/**
 * POST /api/sessions/:sessionId/taps — one tap or a whole offline buffer.
 * Bounded so a runaway client cannot post an unbounded body; the buffer syncs
 * in chunks if it somehow exceeds this.
 */
export const recordTapsRequestSchema = z.object({
  taps: z.array(tapInputSchema).min(1).max(500),
});
export type RecordTapsRequest = z.infer<typeof recordTapsRequestSchema>;

export const recordTapsResponseSchema = z.object({
  ok: z.literal(true),
  /** Ids newly appended by this request. */
  recorded: z.array(z.uuid()),
  /** Ids already in the ledger with identical content — the idempotent replay case. */
  alreadyRecorded: z.array(z.uuid()),
  /**
   * Ids already in the ledger with DIFFERENT content (or another session's) —
   * a client bug, reported rather than silently absorbed. Nothing was written
   * for these; the original rows stand. A correction whose asserted target is
   * missing, in another session, or of an illegal kind is refused here too.
   */
  conflicting: z.array(z.uuid()),
  /**
   * Corrections that LANDED but were not granted supersedes linkage — their
   * target was already superseded (ADR-0010 extension ¶4: a press that lost
   * the race is history, not an error). Inert for resolution; the client logs.
   * Optional so older servers' responses still parse.
   */
  unlinked: z.array(z.uuid()).optional(),
});
export type RecordTapsResponse = z.infer<typeof recordTapsResponseSchema>;

/** One tap as served back from the ledger. */
export const sessionTapSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  kind: z.enum(TAP_KINDS),
  exerciseCode: exerciseCodeShapeSchema.nullable(),
  tappedAt: z.iso.datetime(),
  /** Server receipt time — beside the clinical fact, never instead of it. */
  recordedAt: z.iso.datetime(),
  actor: z.string().min(1),
  provenance: z.enum(EVENT_PROVENANCES),
  detail: z.record(z.string(), z.unknown()).nullable(),
  /**
   * The server-granted correction linkage (ADR-0010 extension ¶1): chains,
   * never branches; the chain head is the record's current statement. Nullish
   * so responses from servers predating migration 009 still parse.
   */
  supersedes: z.uuid().nullish(),
});
export type SessionTap = z.infer<typeof sessionTapSchema>;

export const listTapsResponseSchema = z.object({
  ok: z.literal(true),
  taps: z.array(sessionTapSchema),
});
export type ListTapsResponse = z.infer<typeof listTapsResponseSchema>;

/**
 * What the offline buffer does with a sync response — the client half of the
 * idempotent-replay contract, kept pure here so CI tests it beside the schema
 * it interprets.
 *
 *   - `settledIds`: leave the queue. Newly recorded and already-recorded taps
 *     are landed facts; CONFLICTING ids are also settled — the server refused
 *     them and the original rows stand, so retrying the same id forever would
 *     never succeed. The caller logs/surfaces `conflictingIds` (a client bug),
 *     it does not retry them.
 *   - `retryIds`: ids the batch sent but the response never mentioned (a
 *     drifted or truncated server answer). Nothing proved they landed — they
 *     stay queued for the next sync.
 */
export function partitionTapSyncResult(
  sentIds: readonly string[],
  res: Pick<RecordTapsResponse, 'recorded' | 'alreadyRecorded' | 'conflicting'>,
): { settledIds: string[]; conflictingIds: string[]; retryIds: string[] } {
  const settled = new Set([...res.recorded, ...res.alreadyRecorded, ...res.conflicting]);
  return {
    settledIds: sentIds.filter((id) => settled.has(id)),
    conflictingIds: sentIds.filter((id) => res.conflicting.includes(id)),
    retryIds: sentIds.filter((id) => !settled.has(id)),
  };
}

/**
 * The one readers' rule (ADR-0010 extension ¶5): a ledger's CURRENT
 * STATEMENTS. Drops rows something supersedes, `undo` rows (meta-statements
 * about other rows — a chain headed by one states its event void), and
 * unlinked corrections (`correctionOf` asserted, linkage withheld: a press
 * that lost the race to the seal is visible history, never a statement).
 * Prefill numbers, duplicate windows, and proposals all resolve through this;
 * the full ledger stays served beside it — both rows forever IS the point.
 */
export function resolveCurrentTaps(taps: readonly SessionTap[]): SessionTap[] {
  const superseded = new Set<string>();
  for (const tap of taps) {
    if (tap.supersedes != null) superseded.add(tap.supersedes);
  }
  return taps.filter(
    (tap) =>
      !superseded.has(tap.id) &&
      tap.kind !== 'undo' &&
      !(tap.detail?.['correctionOf'] !== undefined && tap.supersedes == null),
  );
}

// ---- bookmarks (C7) ---------------------------------------------------------

/** ±window anchored on each tap (PRD C7). One constant, shared by server and review surfaces. */
export const BOOKMARK_WINDOW_MS = 1500;

/**
 * An interpretation-stream part that MAY cover a bookmark's window. `anchored`
 * is true when the part carries a recording_start_ms anchor (upload-confirm
 * detail) and the window overlap is therefore exact; unanchored parts are
 * always listed, because nothing in the index can rule them out — the review
 * surface resolves precisely against the envelope's absolute frame timestamps.
 */
export const bookmarkStreamPartSchema = z.object({
  artifactId: z.uuid(),
  partNumber: z.number().int().min(1),
  objectKey: z.string().min(1),
  // 'purged' reaches the review surface for the same reason 'abandoned' does
  // (services/taps.ts): a bookmark whose window falls in a purged part is
  // evidence that the media is GONE, and 'uploaded' there would be a lie about
  // a file the platform itself deleted (increment-9 PR5; plan §1c).
  uploadState: z.enum(['presigned', 'uploaded', 'abandoned', 'purged']),
  anchored: z.boolean(),
});
export type BookmarkStreamPart = z.infer<typeof bookmarkStreamPartSchema>;

/**
 * One derived bookmark (§6 d3: computed at read time, never materialized —
 * a stored bookmark would duplicate facts derivable from taps + parts).
 */
export const bookmarkSchema = z.object({
  tapId: z.uuid(),
  kind: z.enum(TAP_KINDS),
  exerciseCode: exerciseCodeShapeSchema.nullable(),
  tappedAt: z.iso.datetime(),
  windowStartsAt: z.iso.datetime(),
  windowEndsAt: z.iso.datetime(),
  streamParts: z.array(bookmarkStreamPartSchema),
});
export type Bookmark = z.infer<typeof bookmarkSchema>;

export const bookmarksResponseSchema = z.object({
  ok: z.literal(true),
  windowMs: z.number().int().positive(),
  bookmarks: z.array(bookmarkSchema),
});
export type BookmarksResponse = z.infer<typeof bookmarksResponseSchema>;
